import { useCallback } from 'react';
import { useRouter } from 'next/router';

import { Alert, Box, Button, ButtonGroup, CloseButton, Heading, Modal, Paragraph } from '@zillow/constellation-10';

import { useGetListingPreviewUrl } from '../../../../srcLegacy/shared/hooks/queries/listingPreview';
import { useGetListingDetails } from '../../../hooks/queries/properties';

import SpinnerContainer from '../../../components/SpinnerContainer';

import { emitNextEvent } from '../../../analytics/events/modularListing';
import { useAuth } from '../../AuthProvider';

const CURRENT_STEP_KEY = 'listing_preview';

type Props = {
  onClose: () => void;
  onSubmit: (arg?: Record<string, unknown>) => void;
};

const ListingPreviewModal = ({ onClose, onSubmit }: Props): JSX.Element => {
  const router = useRouter();
  const { user } = useAuth();
  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const { data: listingPreviewData, isLoading } = useGetListingPreviewUrl(listingAlias);

  const handleSubmitClick = useCallback(() => {
    emitNextEvent({ currentProperty: listingDetails, user, currentStepKey: CURRENT_STEP_KEY });
    if (listingDetails?.active) {
      onSubmit({ shouldTriggerDeactivate: true });
      onClose();
    } else {
      onSubmit();
    }
  }, [listingDetails?.active]);

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && onClose()}
      header={<Heading level={1}>Listing preview</Heading>}
      body={
        <div data-testid="ListingPreviewModal">
          <Box css={{ margin: '0 {spacing.-400}' }}>
            {isLoading ? (
              <SpinnerContainer />
            ) : (
              <Box css={{ display: 'flex', justifyContent: 'center', backgroundColor: 'bg.soft' }}>
                <iframe
                  width={screen.width >= 1024 ? 1023 : screen.width}
                  height={screen.height}
                  src={listingPreviewData}
                  title="Listing preview of current listing"
                  style={{ border: 'none', backgroundColor: 'white' }}
                />
              </Box>
            )}
            <Alert.Root
              css={{
                position: 'absolute',
                zIndex: '200000',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                left: '50%',
                backgroundColor: 'bg.feedback.info.impact',
                // eslint-disable-next-line curly-quotes/no-straight-quotes
                '& [data-c11n-component="Alert.Icon"]': {
                  color: 'icon.onImpact.feedback.info.strong',
                },
              }}
            >
              <Alert.Body>
                <Paragraph css={{ color: 'text.onImpact.neutral' }}>
                  This preview may not reflect recent changes and may not contain all listing details
                </Paragraph>
              </Alert.Body>
              <Alert.Close>
                <CloseButton onImpact />
              </Alert.Close>
            </Alert.Root>
          </Box>
        </div>
      }
      footer={
        <Box
          css={{
            display: 'flex',
            flexDirection: { base: 'column', md: 'row' },
            flex: '1 1 auto',
            justifyContent: 'end',
            alignItems: { base: 'stretch', md: 'center' },
            gap: '200',
          }}
        >
          <ButtonGroup fluid={{ base: true, md: false }} aria-label="controls">
            <Button
              data-test="back-button"
              aria-label="Cancel"
              tabIndex={0}
              tone="brand"
              emphasis="outlined"
              onClick={onClose}
              type="button"
              css={{ minWidth: '121px' }}
            >
              Back
            </Button>

            <Button
              data-testid="submit-button"
              tone="brand"
              emphasis="filled"
              onClick={handleSubmitClick}
              css={{ minWidth: '121px' }}
            >
              {listingDetails?.active ? 'Deactivate' : 'Publish'}
            </Button>
          </ButtonGroup>
        </Box>
      }
      size={{ base: 'fullScreen' }}
    />
  );
};

export default ListingPreviewModal;
